import { Alert, Box, Chip, CircularProgress, Tooltip, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import * as api from '../../services/api';
import type {
  AssignmentHistoryEntry,
  AssignmentHistoryResponse,
  Criteria,
  Transcription,
} from '../../types';

interface AssignmentHistoryPanelProps {
  criteriaIds: string[];
  criteria: Criteria[];
  transcriptions: Transcription[];
  selectedTranscriptionIds: string[];
  getTranscriptionDisplayLabel: (transcription: Transcription) => string;
}

const formatWhen = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        day: 'numeric',
        month: 'short',
      })
    : '—';

interface HistoryPerson {
  userId: string;
  userName: string;
  status: string | null;
  assignedDate: string | null;
  labelCount: number;
}

const peopleForEntry = (entry: AssignmentHistoryEntry): HistoryPerson[] => {
  const byUser = new Map<string, HistoryPerson>();
  entry.assignments.forEach(assignment => {
    byUser.set(assignment.userId, {
      userId: assignment.userId,
      userName: assignment.userName,
      status: assignment.status,
      assignedDate: assignment.assignedDate,
      labelCount: 0,
    });
  });
  entry.labels.forEach(label => {
    const existing = byUser.get(label.userId);
    if (existing) {
      existing.labelCount = label.labelCount;
    } else {
      byUser.set(label.userId, {
        userId: label.userId,
        userName: label.userName,
        status: null,
        assignedDate: null,
        labelCount: label.labelCount,
      });
    }
  });
  return Array.from(byUser.values());
};

const personDetail = (person: HistoryPerson): string => {
  const parts = [person.userName];
  if (person.status) parts.push(person.status);
  if (person.assignedDate) parts.push(`assigned ${formatWhen(person.assignedDate)}`);
  if (person.labelCount > 0) {
    parts.push(`${person.labelCount} label${person.labelCount === 1 ? '' : 's'}`);
  }
  return parts.join(' · ');
};

const summaryChipLabel = (people: HistoryPerson[]): string => {
  if (people.length === 0) return 'Never assigned';
  if (people.length === 1) return people[0]!.userName;
  return `${people[0]!.userName} + ${people.length - 1}`;
};

const uniqueWord = (count: number, singular: string, plural: string): string =>
  `${count} ${count === 1 ? singular : plural}`;

const AssignmentHistoryPanel = ({
  criteriaIds,
  criteria,
  transcriptions,
  selectedTranscriptionIds,
  getTranscriptionDisplayLabel,
}: AssignmentHistoryPanelProps) => {
  const [histories, setHistories] = useState<Record<string, AssignmentHistoryResponse>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (criteriaIds.length === 0) {
      setHistories({});
      setError(null);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);
    Promise.all(criteriaIds.map(criteriaId => api.fetchAssignmentHistory(criteriaId)))
      .then(responses => {
        if (cancelled) return;
        setHistories(
          Object.fromEntries(responses.map(response => [response.criteriaId, response]))
        );
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load assignment history');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [criteriaIds]);

  const counts = useMemo(() => {
    const entries = criteriaIds.flatMap(criteriaId => histories[criteriaId]?.entries ?? []);
    return {
      previousCount: entries.length,
      uniqueTranscriptCount: new Set(entries.map(entry => entry.transcriptionId)).size,
    };
  }, [criteriaIds, histories]);

  if (criteriaIds.length === 0) return null;

  const countLabel = isLoading
    ? 'Assigned Before'
    : `Assigned Before (${counts.previousCount} · ${uniqueWord(counts.uniqueTranscriptCount, 'unique transcript', 'unique transcripts')})`;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" component="h3">
        {countLabel}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        Who has already been assigned or labelled the selected Criteria.
      </Typography>
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <CircularProgress size={20} />
        </Box>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      {!isLoading && (
        <Box sx={{ maxHeight: 240, overflowY: 'auto', display: 'grid', gap: 0.5 }}>
          {criteriaIds.map(criteriaId => {
            const history = histories[criteriaId];
            const criterionName =
              criteria.find(criterion => criterion.id === criteriaId)?.name ?? 'Unknown';
            if (!history || history.entries.length === 0) {
              return (
                <Typography key={criteriaId} variant="caption" color="text.secondary">
                  No previous assignments or Labels for {criterionName}.
                </Typography>
              );
            }
            return (
              <Box key={criteriaId}>
                {criteriaIds.length > 1 && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {criterionName}
                  </Typography>
                )}
                {history.entries.map(entry => {
                  const transcription = transcriptions.find(
                    item => item.id === entry.transcriptionId
                  );
                  const isSelected = selectedTranscriptionIds.includes(entry.transcriptionId);
                  const people = peopleForEntry(entry);
                  return (
                    <Box
                      key={entry.transcriptionId}
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 0.5,
                        py: 0.25,
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 500, mr: 0.5 }}>
                        {transcription
                          ? getTranscriptionDisplayLabel(transcription)
                          : entry.transcriptionId}
                      </Typography>
                      <Tooltip
                        enterDelay={0}
                        title={
                          people.length === 0 ? (
                            'Never assigned'
                          ) : (
                            <Box>
                              {isSelected && (
                                <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                  Selected
                                </Typography>
                              )}
                              {people.map(person => (
                                <Typography key={person.userId} variant="caption" display="block">
                                  {personDetail(person)}
                                </Typography>
                              ))}
                            </Box>
                          )
                        }
                      >
                        <Chip
                          size="small"
                          color={isSelected ? 'primary' : 'default'}
                          variant="outlined"
                          label={summaryChipLabel(people)}
                        />
                      </Tooltip>
                    </Box>
                  );
                })}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default AssignmentHistoryPanel;
